import { HexColorPicker } from 'react-colorful'
import { useState } from 'react'
import CommonSettings from './CommonSettings'

export default function TableSettings({ block, onUpdate }) {
  const { settings } = block
  const [showHeaderBgPicker, setShowHeaderBgPicker] = useState(false)
  const [showBorderPicker, setShowBorderPicker] = useState(false)

  const rows = settings.rows || []
  const colCount = rows[0]?.length || 3

  const updateCell = (rowIdx, colIdx, value) => {
    const updated = rows.map((row, ri) =>
      ri === rowIdx ? row.map((cell, ci) => ci === colIdx ? value : cell) : [...row]
    )
    onUpdate({ rows: updated })
  }

  const addRow = () => {
    const newRow = Array(colCount).fill('')
    onUpdate({ rows: [...rows, newRow] })
  }

  const removeRow = (rowIdx) => {
    if (rows.length <= 1) return
    onUpdate({ rows: rows.filter((_, i) => i !== rowIdx) })
  }

  const addColumn = () => {
    const updated = rows.map(row => [...row, ''])
    onUpdate({ rows: updated })
  }

  const removeColumn = (colIdx) => {
    if (colCount <= 1) return
    const updated = rows.map(row => row.filter((_, i) => i !== colIdx))
    onUpdate({ rows: updated })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Table Settings</h4>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.showHeader !== false}
            onChange={(e) => onUpdate({ showHeader: e.target.checked })}
          />
          <span style={{ fontSize: '0.85rem' }}>Show Header</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.striped !== false}
            onChange={(e) => onUpdate({ striped: e.target.checked })}
          />
          <span style={{ fontSize: '0.85rem' }}>Striped Rows</span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={addRow}
          style={{
            flex: 1,
            padding: '0.4rem',
            fontSize: '0.8rem',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            background: '#f8fafc',
            cursor: 'pointer'
          }}
        >
          + Row
        </button>
        <button
          onClick={addColumn}
          style={{
            flex: 1,
            padding: '0.4rem',
            fontSize: '0.8rem',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            background: '#f8fafc',
            cursor: 'pointer'
          }}
        >
          + Column
        </button>
      </div>

      <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600' }}>Table Data</h4>

      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} style={{
            marginBottom: '0.5rem',
            padding: '0.5rem',
            background: rowIdx === 0 && settings.showHeader ? '#f0f4ff' : '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b' }}>
                {rowIdx === 0 && settings.showHeader ? 'HEADER' : `Row ${settings.showHeader ? rowIdx : rowIdx + 1}`}
              </span>
              <button
                onClick={() => removeRow(rowIdx)}
                disabled={rows.length <= 1}
                style={{
                  fontSize: '0.7rem',
                  color: rows.length <= 1 ? '#cbd5e1' : '#ef4444',
                  background: 'none',
                  border: 'none',
                  cursor: rows.length <= 1 ? 'default' : 'pointer',
                  padding: '0.15rem 0.35rem'
                }}
              >
                Remove
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              {row.map((cell, colIdx) => (
                <div key={colIdx} style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    value={cell}
                    onChange={(e) => updateCell(rowIdx, colIdx, e.target.value)}
                    placeholder={`Col ${colIdx + 1}`}
                    style={{
                      width: '100%',
                      fontSize: '0.75rem',
                      padding: '0.3rem',
                      fontWeight: rowIdx === 0 && settings.showHeader ? '600' : '400'
                    }}
                  />
                  {rowIdx === 0 && colCount > 1 && (
                    <button
                      onClick={() => removeColumn(colIdx)}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-4px',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        fontSize: '0.55rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1'
                      }}
                      title="Remove column"
                    >
                      x
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h4 style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', fontWeight: '600' }}>Table Style</h4>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Table Width: {settings.tableWidth || '100%'}
        </label>
        <input
          type="range"
          min="30"
          max="100"
          step="5"
          value={parseInt(settings.tableWidth) || 100}
          onChange={(e) => onUpdate({ tableWidth: `${e.target.value}%` })}
          style={{ width: '100%', cursor: 'pointer' }}
        />
      </div>

      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Header Background
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowHeaderBgPicker(!showHeaderBgPicker)}
            style={{
              width: '40px',
              height: '32px',
              backgroundColor: settings.headerBackgroundColor || '#f8f9fa',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          />
          <input
            type="text"
            value={settings.headerBackgroundColor || ''}
            onChange={(e) => onUpdate({ headerBackgroundColor: e.target.value })}
            placeholder="#f8f9fa"
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showHeaderBgPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div style={{ position: 'fixed', inset: 0 }} onClick={() => setShowHeaderBgPicker(false)} />
            <HexColorPicker
              color={settings.headerBackgroundColor || '#f8f9fa'}
              onChange={(color) => onUpdate({ headerBackgroundColor: color })}
            />
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Border Color
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowBorderPicker(!showBorderPicker)}
            style={{
              width: '40px',
              height: '32px',
              backgroundColor: settings.tableBorderColor || '#dee2e6',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          />
          <input
            type="text"
            value={settings.tableBorderColor || ''}
            onChange={(e) => onUpdate({ tableBorderColor: e.target.value })}
            placeholder="#dee2e6"
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showBorderPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div style={{ position: 'fixed', inset: 0 }} onClick={() => setShowBorderPicker(false)} />
            <HexColorPicker
              color={settings.tableBorderColor || '#dee2e6'}
              onChange={(color) => onUpdate({ tableBorderColor: color })}
            />
          </div>
        )}
      </div>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Font Size
        </label>
        <input
          type="text"
          value={settings.fontSize || ''}
          onChange={(e) => onUpdate({ fontSize: e.target.value })}
          placeholder="14px"
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
        />
      </div>

      <CommonSettings settings={settings} onUpdate={onUpdate} />
    </div>
  )
}
